import { findLast } from 'lodash';
import { notNull } from 'utils/type_guards';
import { byId } from 'utils/common';
import reporter from 'utils/reporter';

import type { Dictionary } from '@reduxjs/toolkit';
import type { Dimension, Pair } from 'types/common_types';
import type { ProductCustomOptionsAPIData } from 'types/catalog_types';
import type { CanvasOptionConfiguration } from 'domains/canvases';
import type {
  BaseOptionAPIData,
  OptionAPIData,
  OptionGroupAPIData,
  RawOptionAPIData,
  RawOptionGroupAPIData,
} from '.';

function formatOptionGroup(
  optionGroup: RawOptionGroupAPIData,
  templateId: number | 'order-options'
): OptionGroupAPIData {
  return {
    ...optionGroup,
    parentId: templateId,
    options: optionGroup.options.map(formatOption),
  };
}

function formatOption(option: RawOptionAPIData): OptionAPIData {
  let customOptions = null;

  if (option.custom_options && typeof option.custom_options === 'string') {
    try {
      customOptions = JSON.parse(option.custom_options);
    } catch (error) {
      reporter.addBreadcrumb({
        level: 'info',
        message: 'Failed to parse this custom option JSON',
        data: { json: option.custom_options },
      });

      reporter.captureException(error);
    }
  }

  return { ...option, custom_options: customOptions };
}

/**
 * Gets the ID of the default option in an array of options
 */
function findDefaultOptionId<T extends BaseOptionAPIData>(arr: T[]) {
  const found = arr.find((c) => c.default) || arr.find((c) => !c.read_only && !c.hidden);
  return found?.id;
}

function replaceHiddenOptions(
  optionGroupsById: Dictionary<RawOptionGroupAPIData>,
  configured: CanvasOptionConfiguration
) {
  let hasChanged = false;
  const update = Object.entries(configured).reduce(
    (acc: CanvasOptionConfiguration, pair) => {
      const [ogId, oId] = pair;
      const optionGroup = optionGroupsById[ogId];
      const option = optionGroup?.options.find((o) => o.id === oId);

      if (option && option.hidden) {
        hasChanged = true;
        acc[ogId] = findDefaultOptionId(optionGroup?.options || []);
      }

      return acc;
    },
    { ...configured }
  );

  return { hasChanged, update };
}

function configureDefaultOptions(
  optionGroups: RawOptionGroupAPIData[],
  preconfigured?: CanvasOptionConfiguration
) {
  const requiredOptionGroups = optionGroups.filter((og) => og.required);

  const defaultOptions: CanvasOptionConfiguration = {};
  const keepOptions: CanvasOptionConfiguration = {};

  requiredOptionGroups.forEach((og) => (defaultOptions[og.id] = findDefaultOptionId(og.options)));

  // Go straight to defaults if we had nothing previously configured
  if (!preconfigured) {
    return defaultOptions;
  }

  optionGroups.forEach((og) => {
    const option = preconfigured[og.id];
    const matched = og.options.find((o) => option && o.id === option);

    if (matched && !matched.hidden && !matched.unavailable) {
      keepOptions[og.id] = matched.id;
    }
  });

  return { ...defaultOptions, ...keepOptions };
}

/**
 * Identifies an option group for options that handle crate/freight shipping
 */
function isCrateOrFreightOptionGroup(optionGroup: OptionGroupAPIData) {
  return optionGroup.options.some(
    (o) =>
      o &&
      (o.handling_fee || o.handling_group === 'freight' || o.handling_group === 'acrylicfreight')
  );
}

/**
 * Finds the default option in an option group based on custom size
 */
function findDefaultOptionForCustomSize(optionGroup: OptionGroupAPIData, size: Dimension) {
  // Exclude options that would be disabled at this size before finding the default
  const selectableOptions = optionGroup.options
    .map((possibleOption) => {
      if (!possibleOption.custom_options) return null;

      return customSizeOptionIsDisabled(size.height, size.width, possibleOption.custom_options)
        ? null
        : possibleOption;
    })
    .filter(notNull);

  return findDefaultOptionId(selectableOptions);
}

/**
 * Finds the crate size match in an option group based on custom size
 */
function findDefaultCrateOptionForCustomSize(optionGroup: OptionGroupAPIData, size: Dimension) {
  // Finds the first crate necessary from largest -> smallest
  const match = findLast(optionGroup.options, (crateOption) => {
    const crateOptionCustomOptions = crateOption.custom_options;
    if (!crateOptionCustomOptions) return false;
    return matchesCustomSizeCrateOption(size.height, size.width, crateOptionCustomOptions);
  });

  return match?.id;
}

/**
 * Will return true if any dimension is out of the bounds of the customOptions
 */
function customSizeOptionIsDisabled(
  customHeight: number,
  customWidth: number,
  customOptions: ProductCustomOptionsAPIData
) {
  const { longmax, longmin, shortmin, shortmax } = customOptions;
  if (!longmax || !longmin || !shortmin || !shortmax) return false;

  const long = Math.max(customWidth, customHeight);
  const short = Math.min(customWidth, customHeight);

  return longmax < long || longmin > long || shortmax < short || shortmin > short;
}

/**
 * Will return true if either dimension is within the bounds of the customOptions
 */
function matchesCustomSizeCrateOption(
  customHeight: number,
  customWidth: number,
  customOptions: ProductCustomOptionsAPIData
) {
  const { longmax, longmin, shortmin, shortmax } = customOptions;
  if (!longmax || !longmin || !shortmin || !shortmax) return false;

  const long = Math.max(customWidth, customHeight);
  const short = Math.min(customWidth, customHeight);

  return (long >= longmin && long <= longmax) || (short >= shortmin && short <= shortmax);
}

/**
 * Finds the billing code for any selected options that need a custom price calculation
 */
function findCustomSizeOptionsWithCalculatedPrice(
  optionGroups: OptionGroupAPIData[],
  configuredOptions: CanvasOptionConfiguration
) {
  return Object.entries(configuredOptions)
    .map(([ogid, oid]) => {
      const og = optionGroups.find(byId(parseInt(ogid)));
      if (!og || !oid) return null;

      const o = og.options.find(byId(oid));
      if (!o) return null;

      const bc = o.service.billing_code;

      if (bc.indexOf('CALC') !== -1) {
        return [ogid, bc] as Pair<string, string>;
      }
    })
    .filter(notNull);
}

function findCheapestOption(arr: OptionAPIData[]) {
  const found = arr.reduce((acc: OptionAPIData, o) => {
    if (!acc.id) {
      return o;
    }

    return parseFloat(acc.service.price) < parseFloat(o.service.price) ? acc : o;
  }, arr[0]);

  return { id: found.id, item: found };
}

export default {
  formatOptionGroup,
  formatOption,
  findDefaultOptionId,
  replaceHiddenOptions,
  configureDefaultOptions,
  findDefaultOptionForCustomSize,
  findDefaultCrateOptionForCustomSize,
  isCrateOrFreightOptionGroup,
  customSizeOptionIsDisabled,
  findCustomSizeOptionsWithCalculatedPrice,
  findCheapestOption,
};
